// src/services/works/attributeUnitRules.js
//
// 쿠팡(서플라이어허브) NUMBER 속성의 단위 규칙 — 견적서 속성값을 저장하기 전에 쓴다.
// 쿠팡 검증 2단계는 값 앞의 숫자를 떼고 남은 글자 전체를 단위로 본다
// ("2개 세트" → 단위 "개 세트", "30 도" → 단위 " 도", "해당 없음" → 단위 "해당 없음").
// 카테고리 스키마(attributeUnitEnum)는 비어서 오므로, 허용 단위는 쿠팡 검증 거절 문구
// 「사용 가능한 단위: (...)」에 나온 목록을 단위 그룹(attributeUnitGroup)별로 둔다.
// 목록이 없는 그룹은 「숫자로 시작 + 공백 없이」만 검사한다(모르는 단위를 추정하지 않는다).
//
// 출처 — 쿠팡 validate-step/2 거절 문구 원문(McpProblemReport·ToolFailureEvent, 2026-10 / 시험 계정 실측 2026-10-15):
//   QUANTITY 「수량: 해당 속성에 사용할 수 없는 단위입니다: (개 세트), 사용 가능한 단위: (대,현,자리,종,입,번,봉,회,권,프렛,쌍,캔,인분,정,캡슐,세트,개,선,장,인용,팩,건반,통,매입,박스,Ea,쪽,분류,색,단계,box,병,스틱,음판,회분,set,롤,매,단,구,포,폭,rpm,p,조,피스,마리,공,칸,개입).」
//   LENGTH   「가로길이: 해당 속성에 사용할 수 없는 단위입니다: (인치), 사용 가능한 단위: (mm,km,cm,m).」
//   WEIGHT   「중량: 해당 속성에 사용할 수 없는 단위입니다: (추정 불가), 사용 가능한 단위: (t,lb,g,mg,oz,kg)」
//   VOLUME   「개당 용량: 해당 속성에 사용할 수 없는 단위입니다: (해당 없음), 사용 가능한 단위: (cc,L,ml)」
//   DAYS     출시 연도 «2024년» — 단위 '년' 불허, 사용 가능한 단위: (년도,일,주) (PR261020-9D0339 DALRAEMEORU)
//   실측: 「24인치」(LENGTH)·「2묶음」(QUANTITY) 은 거절 → 목록에 넣지 않는다. 빈 선택 속성(null·키 없음)은 통과.

export const ALLOWED_UNITS_BY_GROUP = {
  QUANTITY: ['대', '현', '자리', '종', '입', '번', '봉', '회', '권', '프렛', '쌍', '캔', '인분', '정', '캡슐', '세트', '개', '선', '장', '인용', '팩', '건반', '통', '매입', '박스', 'Ea', '쪽', '분류', '색', '단계', 'box', '병', '스틱', '음판', '회분', 'set', '롤', '매', '단', '구', '포', '폭', 'rpm', 'p', '조', '피스', '마리', '공', '칸', '개입'],
  LENGTH: ['mm', 'cm', 'm', 'km'],
  WEIGHT: ['mg', 'g', 'kg', 't', 'lb', 'oz'],
  VOLUME: ['ml', 'cc', 'L'],
  DAYS: ['년도', '일', '주']
};

// 뜻이 같은 단위만 바꾼다(켤레 = 쌍). 허용 목록에 있을 때만 쓴다.
const UNIT_SYNONYMS = {
  '켤레': '쌍', '켤레입': '쌍',
  '그램': 'g', '킬로그램': 'kg', '밀리그램': 'mg', '톤': 't',
  '밀리리터': 'ml', '리터': 'L',
  '밀리미터': 'mm', '센티미터': 'cm', '미터': 'm', '킬로미터': 'km',
  '년': '년도'
};

// 단위 뒤의 포장어 — 숫자 없이 이것만 남을 때만 떼어 낸다("2개 세트"→"2개"). 숫자가 더 있으면(「5개입 2팩」) 뜻이 바뀌므로 멈춘다
const PACK_WORDS = ['세트', '묶음', '팩', '개입', 'set'];
// 값 앞의 확정 접두어 — 떼어도 숫자·단위가 그대로 남는 것만
const LEADING_PREFIXES = ['약', '대략', '최대'];

/**
 * 카테고리 스키마 문자열에서 NUMBER 속성의 단위 정보를 뽑는다.
 * @param {string|Object} schemaString - rocketRegInfo.schemaString
 * @returns {Object} { [속성명]: { group, basicUnit, allowed, mandatory } }
 */
export function buildUnitMeta(schemaString) {
  const meta = {};
  if (!schemaString) return meta;
  let schema = schemaString;
  if (typeof schemaString === 'string') {
    try {
      schema = JSON.parse(schemaString);
    } catch (e) {
      console.error('[attributeUnitRules] 스키마 파싱 실패:', e.message);
      return meta;
    }
  }
  const pp = schema?.properties?.productPage?.properties;
  const lists = [
    pp?.commonAttributes?.properties?.exposedAttributes?.allOf,
    pp?.unexposedAttributes?.allOf
  ];
  for (const list of lists) {
    for (const config of list || []) {
      const an = config?.contains?.properties?.attributeName;
      const name = an?.enum?.[0];
      if (!name || an.attributeDataType !== 'NUMBER') continue;
      const group = an.attributeUnitGroup || '';
      const basic = String(an.attributeUnit || '').match(/\(([^)]+)\)/);
      meta[name] = {
        group,
        basicUnit: basic ? basic[1] : '',
        allowed: ALLOWED_UNITS_BY_GROUP[group] || null,
        mandatory: an.mandatory === true || an.requirement === '필수'
      };
    }
  }
  return meta;
}

/**
 * 카테고리 스키마에서 «필수» 비노출 속성 이름만 뽑는다 — 견적서 최종 검증(schemaValidator)과 같은 기준(requirement === '필수').
 * 비노출 자동 생성을 꺼 둔 계정도 이것만은 채운다(쿠팡이 필수로 요구한다).
 * @param {string|Object} schemaString - rocketRegInfo.schemaString
 * @returns {Set<string>}
 */
export function requiredUnexposedNames(schemaString) {
  const names = new Set();
  if (!schemaString) return names;
  let schema = schemaString;
  if (typeof schemaString === 'string') {
    try {
      schema = JSON.parse(schemaString);
    } catch (e) {
      console.error('[attributeUnitRules] 스키마 파싱 실패:', e.message);
      return names;
    }
  }
  for (const config of schema?.properties?.productPage?.properties?.unexposedAttributes?.allOf || []) {
    const an = config?.contains?.properties?.attributeName;
    if (an?.requirement === '필수' && an.enum?.[0]) names.add(an.enum[0]);
  }
  return names;
}

/** 프롬프트에 넣을 한 줄(NUMBER 속성만). 해당 없으면 '' */
export function unitHint(m) {
  if (!m) return '';
  const eg = m.basicUnit ? `(예: "1${m.basicUnit}")` : "";
  const base = `- 숫자+단위를 공백 없이 붙여 쓴다${eg}. 단위 뒤에 다른 말("세트", "묶음", "(해당 없음)" 등)을 붙이지 않는다.`;
  const allowed = m.allowed ? ` 허용 단위(이 중 하나만): ${m.allowed.join(', ')}` : (m.basicUnit ? ` 기본 단위: ${m.basicUnit}` : '');
  const empty = m.mandatory ? ' 필수 속성이라 반드시 숫자로 채운다.' : ' 값을 모르면 글자 대신 null.';
  return `${base}${allowed ? `${allowed}.` : ""}${empty}`;
}

function resolveUnit(u, allowed) {
  if (!u) return null;
  if (allowed.includes(u)) return u;
  const syn = UNIT_SYNONYMS[u];
  if (syn && allowed.includes(syn)) return syn;
  const ci = allowed.filter(a => a.toLowerCase() === u.toLowerCase());
  return ci.length === 1 ? ci[0] : null;
}

/**
 * 속성값 하나를 허용 단위로 맞춘다. 확정 규칙으로 못 맞추면 ok:false + 사유.
 * @returns {{ok: boolean, value?: (string|null), changed?: boolean, reason?: string}}
 */
export function normalizeUnitValue(value, m) {
  if (!m || value === null || value === undefined) return { ok: true, value, changed: false };
  let str = String(value).trim();
  if (str === '') return { ok: true, value, changed: false };

  // 확정 접두어(약/대략/최대)만 뗀다
  for (const p of LEADING_PREFIXES) {
    const re = new RegExp(`^${p}\\s*(?=\\d)`);
    if (re.test(str)) { str = str.replace(re, ''); break; }
  }

  const match = str.match(/^(\d+(?:[.,]\d+)*)\s*(.*)$/);
  if (!match) {
    // 숫자가 하나도 없는 글자("해당 없음", "추정 불가", "N/A")는 값이 없다는 뜻 — 선택 속성이면 비우고 사유를 남긴다(빈 선택 속성은 쿠팡 2단계 통과 실측)
    if (!/\d/.test(str) && !m.mandatory) {
      return { ok: true, value: null, changed: true, reason: `숫자가 없는 값 "${str}" 을 비움(선택 속성)` };
    }
    return { ok: false, reason: `숫자로 시작하지 않는 값 "${str}"` };
  }
  const num = match[1];
  const unit = match[2].trim();
  // 수량 «300개+수납함» / «2켤레 + 파우치» — 「숫자+단위」 뒤 «+ 딸린 물건» 은 떼어 extra 로 돌려준다(구성품 칸으로 옮기는 건 placeExtraComponent)
  const plus = m.group === 'QUANTITY' ? unit.match(/^([^+\d]+?)\s*\+\s*([^+]+)$/) : null;
  if (plus) {
    const head = normalizeUnitValue(`${num}${plus[1].trim()}`, m);
    const extra = plus[2].trim();
    if (!head.ok || head.value === null || !extra) {
      return { ok: false, reason: `"${str}" 의 단위 "${unit}" 는 쿠팡 허용 단위(${m.allowed.join(', ')})로 확정해 바꿀 수 없음` };
    }
    return { ok: true, value: head.value, changed: true, extra };
  }
  if (!m.allowed || unit === '') {
    const v = `${num}${unit}`;
    return { ok: true, value: v, changed: v !== value };
  }

  let resolved = resolveUnit(unit, m.allowed);
  if (!resolved) {
    // 「허용 단위 + 포장어」만 허용: 첫 단어(또는 붙여 쓴 앞부분)가 허용 단위이고 나머지가 숫자 없는 포장어뿐일 때
    const isPackRest = (rest) => {
      const words = rest.trim().split(/\s+/).filter(Boolean);
      return words.length > 0 && words.every(w => PACK_WORDS.some(p => p.toLowerCase() === w.toLowerCase()));
    };
    const tokens = unit.split(/\s+/);
    if (tokens.length > 1 && isPackRest(tokens.slice(1).join(' '))) resolved = resolveUnit(tokens[0], m.allowed);
    if (!resolved) {
      for (const p of PACK_WORDS) {
        if (unit.length > p.length && unit.toLowerCase().endsWith(p.toLowerCase())) {
          resolved = resolveUnit(unit.slice(0, -p.length), m.allowed);
          if (resolved) break;
        }
      }
    }
  }
  if (!resolved) return { ok: false, reason: `"${str}" 의 단위 "${unit}" 는 쿠팡 허용 단위(${m.allowed.join(', ')})로 확정해 바꿀 수 없음` };
  const v = `${num}${resolved}`;
  return { ok: true, value: v, changed: v !== value };
}

// 재질 낱말 — 속성 번역 프롬프트(quotationService.requestTranslateAttributes)가 싣는다. 글자대로 옮기면 틀리는 낱말만 둔다.
// 출처 — 고객 문제 보고 PR261021-395EA3(daycozy01 · 2026-10-21 윈 3495②): 铜板不干胶 이 «구리판 스티커»로 옮겨짐(바름 = 아트지 스티커).
//   铜版纸(铜板纸 로도 씀) = 겉을 코팅한 인쇄 용지(아트지) — 여기 «铜» 은 구리가 아니다 · 不干胶 = 뒷면 접착 스티커(라벨)
export const MATERIAL_TERMS = [
  ['铜版纸', '아트지(코팅지)'],
  ['铜板纸', '아트지(코팅지)'],
  ['铜版不干胶', '아트지 스티커'],
  ['铜板不干胶', '아트지 스티커'],
  ['不干胶', '스티커(접착 라벨)'],
];
export function materialTermsPrompt() {
  return MATERIAL_TERMS.map(([zh, ko]) => `   - ${zh} -> ${ko}`).join('\n');
}

// 공통 비노출 속성 지침 — 원문 근거가 있으면 원문 · 없을 때만 추정하고 그 이름을 guessed 에(PR261021-761EC8 «4317 비노출속성도»).
// 예전 «절대 null 금지 · 추측해도 된다» 는 근거 없는 값을 근거 있는 값과 구분 없이 지어냈다. null 금지는 그대로(필수 검증)
export function unexposedSourceRule() {
  return [
    '1. **원문 우선**: 1688 상품 속성 정보·남긴 옵션 원문·상품명에 근거가 있으면 그 원문 값을 그대로 쓴다(단위만 예시에 맞춘다). 근거가 있는데 다른 값으로 바꾸지 않는다.',
    '2. **근거가 없을 때만 추정**: 근거가 없을 때만 상품과 가장 가까운 값으로 추정하고, 그 속성 이름을 응답의 "guessed" 배열에 넣는다.',
    '3. **null 금지**: 입력예시 속성은 null 로 내지 않는다(추정한 값이면 guessed 에 표시).',
  ].join('\n');
}

// 확장 AI 응답(batchResults[0].result · processedText(코드 울타리) · 객체)에서 guessed 이름 — 스키마에 있는 문자열만 · 중복 0
export function guessedNames(result, schemaAttrs) {
  const b = result && Array.isArray(result.batchResults) ? result.batchResults[0] : null;
  let parsed = b ? b.result : result;
  if (!parsed && b && typeof b.processedText === 'string') {
    try {
      parsed = JSON.parse(b.processedText.replace(/```json\n|\n```/g, '').replace(/```\n|\n```/g, '').trim());
    } catch (e) {
      console.error('guessed 읽기 실패(응답 JSON 아님):', e.message);
      return [];
    }
  }
  const names = new Set((schemaAttrs || []).map(a => a && a.name));
  const list = parsed && Array.isArray(parsed.guessed) ? parsed.guessed : [];
  return [...new Set(list.filter(n => typeof n === 'string' && names.has(n)))];
}

export const COMPONENT_ATTR = '구성품';

/**
 * 카테고리 스키마에서 속성이 어느 목록에 있는지 — 'exposed' | 'unexposed' | null
 * @param {string|Object} schemaString - rocketRegInfo.schemaString
 * @param {string} name - 속성명
 */
export function attributeListOf(schemaString, name) {
  if (!schemaString) return null;
  let schema = schemaString;
  if (typeof schemaString === 'string') {
    try {
      schema = JSON.parse(schemaString);
    } catch (e) {
      console.error('[attributeUnitRules] 스키마 파싱 실패:', e.message);
      return null;
    }
  }
  const pp = schema?.properties?.productPage?.properties;
  const has = (list) => (list || []).some(c => c?.contains?.properties?.attributeName?.enum?.[0] === name);
  if (has(pp?.commonAttributes?.properties?.exposedAttributes?.allOf)) return 'exposed';
  if (has(pp?.unexposedAttributes?.allOf)) return 'unexposed';
  return null;
}

/**
 * 수량에서 떼어 낸 «딸린 물건»(extras)을 «구성품» 속성에 넣는다 — 구성품이 있고 비어 있을 때만.
 * 구성품이 없거나 이미 값이 있거나 그 목록을 쓸 수 없으면 넣지 않고 warning 을 돌려준다(덮어쓰지 않는다).
 * @param {Object} attributeData - { exposedAttributes, unexposedAttributes }
 * @param {string[]} extras - 떼어 낸 글자들
 * @param {string|Object} schemaString - rocketRegInfo.schemaString
 * @param {Object} [opts] - { canWriteUnexposed: boolean } (기본 true)
 * @returns {{ data: Object, warning: (string|null) }}
 */
export function placeExtraComponent(attributeData, extras, schemaString, opts = {}) {
  const text = (extras || []).filter(Boolean).join(' + ');
  if (!text) return { data: attributeData, warning: null };
  const drop = (why) => ({ data: attributeData, warning: `수량에서 뗀 "${text}" 를 ${why} 버림` });
  const where = attributeListOf(schemaString, COMPONENT_ATTR);
  if (!where) return drop(`이 카테고리에 «${COMPONENT_ATTR}» 속성이 없어`);
  if (where === 'unexposed' && opts.canWriteUnexposed === false) return drop('비노출 속성 적용이 꺼져 있어');
  const key = where === 'exposed' ? 'exposedAttributes' : 'unexposedAttributes';
  const list = attributeData[key] || [];
  const idx = list.findIndex(a => a.attributeName === COMPONENT_ATTR);
  if (idx >= 0) {
    const cur = list[idx].attributeValue || list[idx].value;
    if (cur !== null && cur !== undefined && String(cur).trim() !== '') return drop(`«${COMPONENT_ATTR}» 에 이미 값("${cur}")이 있어`);
    const next = { ...list[idx], attributeValue: text };
    if ('value' in list[idx]) next.value = text;
    return { data: { ...attributeData, [key]: list.map((a, i) => (i === idx ? next : a)) }, warning: null };
  }
  return { data: { ...attributeData, [key]: [...list, { attributeName: COMPONENT_ATTR, attributeValue: text }] }, warning: null };
}
